import * as Sentry from '@sentry/nextjs';
import { BuilderError } from '@/types/errors';

/**
 * Report a caught error to Sentry UNLESS it is a BuilderError.
 *
 * `restRequest` reports its own failures and rethrows them as a `BuilderError`, so a
 * caller's catch that re-reported one would double-report the same incident. Use this
 * in `restRequest` callers so only caller-origin failures (e.g. post-fetch processing
 * that runs alongside the fetch) reach Sentry — exactly once, tagged consistently.
 *
 * Pass `context` for debugging detail, and an optional `message` label to keep the
 * Sentry title descriptive — it's prepended to the original error message.
 *
 * The wrapped BuilderError is built from the final message string so its own
 * message and stack header stay aligned, and the original error is attached as
 * `cause` so Sentry's linkedErrors surfaces it as a chained exception with the
 * original throw-site stack preserved.
 */
export function captureUnlessBuilderError(
	error: unknown,
	context?: Record<string, unknown>,
	message?: string,
): void {
	if (error instanceof BuilderError) {
		return;
	}

	const baseMessage = error instanceof Error ? error.message : String(error);
	const builderError = new BuilderError(message ? `${message}: ${baseMessage}` : baseMessage);
	if (error instanceof Error) {
		builderError.cause = error;
	}

	Sentry.withScope((scope) => {
		scope.setTag('api', 'builder');
		// Caller's post-fetch processing threw, not the Builder fetch — keeps these filterable on error_type.
		scope.setTag('error_type', 'caller_processing');
		scope.setLevel('error');

		if (context) {
			scope.setContext('caller_context', context);
		}

		Sentry.captureException(builderError);
	});
}
